/**
 * How a quantity column is treated during clean power allocation when a
 * pipeline result row is split into clean and dirty portions.
 *
 * - `ZeroOnClean`: The column is zeroed on clean rows and prorated on dirty
 *   rows. Used for market-based emissions that are contractually eliminated by
 *   clean power. Examples: `quantity_kg_co2e`, `co2_quantity_kg_co2e`,
 *   `biogenic_co2_quantity_kg_co2e`.
 *
 * - `Prorate`: The column is prorated (multiplied by allocation ratio) on both
 *   clean and dirty rows. Used for location-based emissions that reflect
 *   physical grid reality regardless of contracts, and for non-emissions
 *   quantities. Examples: `allocation_location_quantity_kg_co2e`,
 *   `ch4_quantity_kg_co2e`.
 *
 * - `AllocateKwh`: The column is set to the allocated kWh amount on clean rows
 *   and the remaining kWh on dirty rows. Only used for `kwh`, which is the unit
 *   of allocation and cannot simply be prorated — clean rows get the exact
 *   allocated amount, dirty rows get what remains.
 *
 * - `SetAvoided`: The column is computed as the avoided emissions on clean rows
 *   and set to null on dirty rows. Only used for
 *   `clean_power_avoided_quantity_kg_co2e`.
 *
 * - `NoTreatment`: The column is not modified during clean power allocation. It
 *   carries forward as-is from the original row.
 *
 * we use enum instead of makeBetterEnum because it doesn't play well with
 * Temporal workflow types.
 */
export enum CleanPowerTreatment {
  ZeroOnClean = 'ZeroOnClean',
  Prorate = 'Prorate',
  AllocateKwh = 'AllocateKwh',
  SetAvoided = 'SetAvoided',
  NoTreatment = 'NoTreatment',
}

/**
 * Floating point comparison threshold for clean power budget exhaustion.
 * Tiny remainders from floating point arithmetic (e.g. 1e-15) shouldn't
 * count as unallocated kWh.
 */
export const CLEAN_POWER_BUDGET_EPSILON = 1e-9;

/**
 * The SQL column name for kWh, used to identify the allocation unit
 * column during clean power treatment.
 */
export const KWH_COLUMN = 'kwh';

/**
 * Prefix for dimensional columns in EAC allocation data parquets.
 * e.g., dim:building_name, dim:country
 */
export const EACS_BI_DIMENSIONS_PREFIX = 'dim:';

/**
 * The unit value that identifies rows eligible for clean power allocation.
 */
export const CLEAN_POWER_UNIT = 'kwh';

/**
 * Column name for the JSON-aggregated EAC budgets in grouped allocation parquets.
 * Each value is a JSON array of {eac_id, amount_kwh} objects.
 */
export const EAC_BUDGETS_COLUMN = 'eac_budgets';

/**
 * Column name for the footprint slice ID in grouped allocation parquets.
 */
export const FOOTPRINT_SLICE_ID_COLUMN = 'footprint_slice_id';

/**
 * Sentinel footprint_slice_id for applicable rows that did not match
 * any allocation slice during the join step.
 */
export const UNMATCHED_SLICE_ID = '__unmatched__';
